import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCartStore } from '../stores/cartStore';
import { portaleAPI } from '../utils/api';
import {
  TrashIcon,
  MinusIcon,
  PlusIcon,
  ShoppingCartIcon,
  CubeIcon,
} from '@heroicons/react/24/outline';
import { formatCurrency } from '../utils/constants';
import toast from 'react-hot-toast';

const Cart: React.FC = () => {
  const navigate = useNavigate();
  const items = useCartStore((s) => s.items);
  const updateQuantity = useCartStore((s) => s.updateQuantity);
  const updateNote = useCartStore((s) => s.updateNote);
  const removeItem = useCartStore((s) => s.removeItem);
  const clearCart = useCartStore((s) => s.clearCart);
  const getTotale = useCartStore((s) => s.getTotale);

  const [titolo, setTitolo] = useState('');
  const [note, setNote] = useState('');

  // Get profile to check mostra_prezzi
  const { data: profileData } = useQuery({
    queryKey: ['customer-profile'],
    queryFn: () => portaleAPI.getProfile(),
  });
  const mostraPrezzi = profileData?.data?.mostra_prezzi ?? false;

  const createOrderMutation = useMutation({
    mutationFn: () =>
      portaleAPI.createOrder({
        titolo,
        note: note || undefined,
        items: items.map((i) => ({
          prodotto_id: i.prodotto_id,
          quantita: i.quantita,
          note: i.note,
        })),
      }),
    onSuccess: () => {
      toast.success('Ordine inviato con successo!');
      clearCart();
      navigate('/my-orders');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore durante l\'invio dell\'ordine');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!titolo.trim()) {
      toast.error('Inserisci un titolo per l\'ordine');
      return;
    }
    if (items.length === 0) {
      toast.error('Il carrello è vuoto');
      return;
    }
    createOrderMutation.mutate();
  };

  if (items.length === 0) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Carrello</h1>
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <ShoppingCartIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Il carrello è vuoto</h3>
          <p className="text-gray-500 mb-4">Sfoglia il catalogo per aggiungere prodotti</p>
          <button
            onClick={() => navigate('/catalog')}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
          >
            Vai al Catalogo
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Carrello</h1>
          <p className="text-gray-600 mt-1">{items.length} prodott{items.length === 1 ? 'o' : 'i'} nel carrello</p>
        </div>
        <button
          onClick={() => navigate('/catalog')}
          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
        >
          Continua a sfogliare
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {items.map((item) => (
              <div key={item.prodotto_id} className="bg-white shadow rounded-lg p-4">
                <div className="flex gap-4">
                  {/* Image */}
                  <div className="h-20 w-20 flex-shrink-0 bg-gray-100 rounded-md flex items-center justify-center">
                    {item.prodotto.immagine_default_url ? (
                      <img
                        src={item.prodotto.immagine_default_url}
                        alt={item.prodotto.nome}
                        className="h-full w-full object-cover rounded-md"
                      />
                    ) : (
                      <CubeIcon className="h-8 w-8 text-gray-300" />
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-gray-900 truncate">
                      {item.prodotto.nome}
                    </h3>
                    {item.prodotto.codice && (
                      <p className="text-xs text-gray-500">Cod. {item.prodotto.codice}</p>
                    )}

                    {mostraPrezzi && (item.prodotto.prezzo_finale != null) && (
                      <p className="text-sm font-medium text-primary-600 mt-1">
                        {formatCurrency(item.prodotto.prezzo_finale)} / {item.prodotto.unita_misura}
                      </p>
                    )}

                    {/* Quantity control */}
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        type="button"
                        onClick={() => updateQuantity(item.prodotto_id, item.quantita - 1)}
                        disabled={item.quantita <= 1}
                        className="p-1 rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50"
                      >
                        <MinusIcon className="h-4 w-4" />
                      </button>
                      <input
                        type="number"
                        min={1}
                        value={item.quantita}
                        onChange={(e) => updateQuantity(item.prodotto_id, parseInt(e.target.value) || 1)}
                        className="w-16 text-center border border-gray-300 rounded-md text-sm py-1"
                      />
                      <button
                        type="button"
                        onClick={() => updateQuantity(item.prodotto_id, item.quantita + 1)}
                        className="p-1 rounded border border-gray-300 hover:bg-gray-100"
                      >
                        <PlusIcon className="h-4 w-4" />
                      </button>
                    </div>

                    {/* Note */}
                    <input
                      type="text"
                      placeholder="Note (opzionale)..."
                      value={item.note || ''}
                      onChange={(e) => updateNote(item.prodotto_id, e.target.value)}
                      className="mt-2 block w-full border border-gray-300 rounded-md text-sm py-1 px-2 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* Subtotal + Remove */}
                  <div className="flex flex-col items-end justify-between">
                    {mostraPrezzi && (item.prodotto.prezzo_finale != null) && (
                      <span className="text-sm font-semibold text-gray-900">
                        {formatCurrency(item.prodotto.prezzo_finale * item.quantita)}
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => removeItem(item.prodotto_id)}
                      className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white shadow rounded-lg p-6 sticky top-6 space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Riepilogo Ordine</h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Titolo ordine *
                </label>
                <input
                  type="text"
                  required
                  value={titolo}
                  onChange={(e) => setTitolo(e.target.value)}
                  placeholder="Es. Ordine materiale ufficio"
                  className="block w-full border border-gray-300 rounded-md text-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Note (opzionale)
                </label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  placeholder="Note aggiuntive..."
                  className="block w-full border border-gray-300 rounded-md text-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Prodotti</span>
                  <span>{items.length}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Quantità totale</span>
                  <span>{items.reduce((s, i) => s + i.quantita, 0)}</span>
                </div>
                {mostraPrezzi && (
                  <div className="flex justify-between text-lg font-bold text-gray-900 pt-2 border-t">
                    <span>Totale</span>
                    <span>{formatCurrency(getTotale())}</span>
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={createOrderMutation.isPending || !titolo.trim()}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {createOrderMutation.isPending ? 'Invio in corso...' : 'Invia Ordine'}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Cart;
